/**
 * Plan detail modal — one plan, read top to bottom in the order a reviewer
 * needs it: the review state with its actions (approve / decline with a note
 * / withdraw, mirroring the server transition map), the three facts that
 * define the plan (when, which moment, what format), who it's for, what may
 * be shared with advertisers, the confidential notes, and the status history
 * behind a disclosure. Loads its own data by id so email deep links
 * (?tab=calendar&entry=<id>) work before the list has been fetched.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from '../../ui';
import { bannerStyle } from '../MediaPlans/formStyles';
import {
  changeCalendarEntryStatus,
  deleteCalendarEntry,
  getCalendarEntry,
} from '../../../services/tentpoleCalendarApi';
import ReviewStatusPill from './ReviewStatusPill';
import {
  advertiserSummaryOf,
  CALENDAR_FIELD_LABELS,
  entryDisplayParts,
  fmtDate,
  fmtDateTime,
  fmtDaysAgo,
  fmtPlannedRange,
  fmtTentpoleWindow,
  planStatusOf,
  recurrenceOf,
  targetChannelLabels,
  targetScopeLabel,
  tentpoleLabel,
} from './shared';
import {
  CalendarEntry,
  CalendarEntryStatus,
  CONTENT_TYPE_LABELS,
  ENTRY_STATUS_LABELS,
  ENTRY_STATUS_NEXT,
  PLAN_STATUS_LABELS,
  RECURRENCE_LABELS,
  Tentpole,
} from './types';

interface EntryDetailProps {
  entryId: string;
  canManage: boolean;
  /** Sanitized row used for read-only detail; confidential fetches are manager-only. */
  summaryEntry?: CalendarEntry | null;
  summaryTentpole?: Tentpole | null;
  onClose: () => void;
  /** Called with the updated entry after any mutation so the list refreshes. */
  onUpdated: (entry: CalendarEntry) => void;
  /** Removes a deleted entry from every locally loaded dashboard page. */
  onDeleted: (entryId: string) => void;
  onEdit: (entry: CalendarEntry) => void;
  /** Custom-moment entries only: open TentpoleEditor pre-filled to promote. */
  onPromote: (entry: CalendarEntry) => void;
  onReviewChanged?: () => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

/** One of the three key facts: a label, the value, and a quieter qualifier. */
const Fact: React.FC<{ name: string; value: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode }> = ({
  name,
  value,
  sub,
  children,
}) => (
  <div style={{ minWidth: 0 }}>
    <div style={microLabelStyle}>{name}</div>
    <div style={factValueStyle}>{value}</div>
    {sub && <div style={factSubStyle}>{sub}</div>}
    {children}
  </div>
);

const ACTION_LABELS: Partial<Record<CalendarEntryStatus, string>> = {
  approved: 'Approve',
  withdrawn: 'Withdraw',
};

const EntryDetail: React.FC<EntryDetailProps> = ({
  entryId,
  canManage,
  summaryEntry,
  summaryTentpole,
  onClose,
  onUpdated,
  onDeleted,
  onEdit,
  onPromote,
  onReviewChanged,
  notify,
}) => {
  const [entry, setEntry] = useState<CalendarEntry | null>(summaryEntry ?? null);
  const [tentpole, setTentpole] = useState<Tentpole | null>(summaryTentpole ?? null);
  // Id of the confidential manager projection held in `entry`. Until it is
  // this entry's, `entry` is the sanitized list row (no internal note), so the
  // footer actions that hand it on to an editor stay disabled.
  const [fullEntryId, setFullEntryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(canManage);
  const [reloadCount, setReloadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [declineNote, setDeclineNote] = useState('');
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const deleteConfirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canManage) return;
    setEntry(summaryEntry ?? null);
    setTentpole(summaryTentpole ?? null);
    setError(summaryEntry ? null : "This plan isn't in the loaded calendar rows. Ask a calendar manager to open the full detail.");
    setLoading(false);
  }, [canManage, summaryEntry, summaryTentpole]);

  // A different entry loads behind the spinner.
  useEffect(() => {
    if (canManage) setLoading(true);
  }, [entryId, canManage]);

  // Every calendar refresh hands this modal a new summary object, so refetch
  // only when the row actually changed (its updatedAt) or a reload is asked
  // for. This never turns the spinner on: an entry already on screen updates
  // in place, and the body (with a half-typed decline note) stays mounted.
  const summaryUpdatedAt = summaryEntry?.updatedAt;
  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    getCalendarEntry(entryId)
      .then((res) => {
        if (cancelled) return;
        setEntry(res.entry);
        setTentpole(res.tentpole);
        setFullEntryId(res.entry.id);
        setError(null);
      })
      .catch((err) => !cancelled && setError(err?.message || 'Failed to load the plan'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [entryId, canManage, summaryUpdatedAt, reloadCount]);

  // The delete confirm opens from the footer; bring it into view however far
  // the body is scrolled. (jsdom has no scrollIntoView.)
  useEffect(() => {
    if (confirmingDelete) deleteConfirmRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [confirmingDelete]);

  const fullEntryLoaded = fullEntryId === entryId;

  const applyUpdate = (updated: CalendarEntry) => {
    setEntry(updated);
    onUpdated(updated);
  };

  const moveTo = async (to: CalendarEntryStatus, note?: string) => {
    if (!entry) return;
    setBusy(true);
    try {
      // Refused (409 stale) if the plan changed after this copy was loaded.
      const updated = await changeCalendarEntryStatus(entry.id, to, note, entry.updatedAt);
      applyUpdate(updated);
      onReviewChanged?.();
      notify('success', to === 'declined' ? 'Plan declined' : `Marked ${ENTRY_STATUS_LABELS[to]}`);
      setConfirmingDecline(false);
      setDeclineNote('');
    } catch (err: any) {
      notify('error', 'Status change failed', err?.message);
      if (err?.code === 'stale') {
        // Show the plan as it is now, and refresh the calendar behind us.
        setReloadCount((count) => count + 1);
        onReviewChanged?.();
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!entry) return;
    setBusy(true);
    try {
      await deleteCalendarEntry(entry.id);
      onDeleted(entry.id);
      notify('success', 'Plan deleted', `“${entry.title}” was permanently removed from all calendars.`);
      onClose();
      onReviewChanged?.();
    } catch (err: any) {
      notify('error', 'Delete failed', err?.message);
    } finally {
      setBusy(false);
    }
  };

  const nextStatuses = entry ? ENTRY_STATUS_NEXT[entry.status] ?? [] : [];
  const statusHistory = entry?.statusHistory ?? [];
  const channelLabels = entry ? targetChannelLabels(entry) : [];
  const wasResubmitted = statusHistory.some((h) => h.from === 'approved' && h.to === 'submitted');
  const requiresManagerReview = !!(entry?.requiresManagerReview || entry?.migrationState);

  // The review line: while a plan waits, when it arrived and how long ago;
  // afterwards, who moved it to where it is now.
  const latestTo = (status: CalendarEntryStatus) => [...statusHistory].reverse().find((h) => h.to === status);
  const lastChange = entry && entry.status !== 'submitted' ? latestTo(entry.status) : undefined;
  let reviewLine = '';
  let reviewLineTitle: string | undefined;
  if (entry && lastChange) {
    reviewLine = `${ENTRY_STATUS_LABELS[lastChange.to]} ${fmtDate(lastChange.at)} by ${lastChange.byEmail}`;
    reviewLineTitle = fmtDateTime(lastChange.at);
  } else if (entry) {
    const arrivedAt = (entry.status === 'submitted' && latestTo('submitted')?.at) || entry.createdAt;
    const origin = wasResubmitted && entry.status === 'submitted' ? 'Re-submitted'
      : entry.source === 'admin' ? `Added by ${entry.createdBy || 'CG'}${entry.createdVia === 'mcp' ? ' via Claude' : ''}`
        : entry.source === 'creator' ? 'Submitted' : 'Created';
    reviewLine = `${origin} ${fmtDate(arrivedAt)} · ${fmtDaysAgo(arrivedAt)}`;
    reviewLineTitle = fmtDateTime(arrivedAt);
  }

  const reviewActions = entry && canManage ? (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {nextStatuses
        .filter((s) => s !== 'declined' && !(s === 'approved' && requiresManagerReview))
        .map((s) => (
          <Button
            key={s}
            size="sm"
            variant={s === 'approved' ? (entry.status === 'submitted' ? 'primary' : 'subtle') : 'ghost'}
            disabled={busy}
            onClick={() => moveTo(s)}
          >
            {ACTION_LABELS[s] ?? ENTRY_STATUS_LABELS[s]}
          </Button>
        ))}
      {nextStatuses.includes('declined') && (
        <Button
          size="sm"
          variant={entry.status === 'submitted' ? 'subtle' : 'ghost'}
          disabled={busy}
          onClick={() => setConfirmingDecline(true)}
        >
          Decline
        </Button>
      )}
    </div>
  ) : null;

  const recurrence = entry ? recurrenceOf(entry) : 'unknown';
  const planStatus = entry ? planStatusOf(entry) : 'unknown';

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={entry ? entryDisplayParts(entry).primary : 'Plan'}
      title={entry ? entry.title : 'Loading…'}
      width={720}
      closeOnEsc={false}
      footer={
        entry && canManage ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <Button
              size="sm"
              variant="ghost"
              style={{ color: 'var(--danger)' }}
              disabled={busy || !fullEntryLoaded}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete plan
            </Button>
            <Button size="sm" variant="subtle" disabled={busy || !fullEntryLoaded} onClick={() => onEdit(entry)}>
              Edit plan
            </Button>
          </div>
        ) : undefined
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : error || !entry ? (
        <div style={{ color: 'var(--danger)', padding: 20 }}>{error || 'Plan not found'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {confirmingDelete && (
            <div
              ref={deleteConfirmRef}
              role="alert"
              aria-label="Confirm permanent deletion"
              style={{
                background: 'var(--danger-soft)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
                <strong>Permanently delete “{entry.title}”?</strong>{' '}
                It will be removed from the Admin calendar and every Creator or Agency calendar where it appears.
                This cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" variant="destructive" disabled={busy} onClick={remove}>
                  {busy ? 'Deleting…' : 'Delete permanently'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => setConfirmingDelete(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {requiresManagerReview && (
            <div role="status" style={{ ...bannerStyle('warning'), display: 'block' }}>
              <strong>Manager decision required.</strong>{' '}
              <span style={{ color: 'var(--fg-2)', fontWeight: 500 }}>
                {entry.migrationState === 'quarantined'
                  ? 'This legacy plan is quarantined and stays internal-only until its origin and channel scope are resolved.'
                  : 'Open Edit plan and save its channel scope explicitly before approving this legacy All channels submission.'}
              </span>
            </div>
          )}

          {/* Review — tinted while it waits on CG, quiet once decided. */}
          <div style={{ ...reviewPanelStyle, ...REVIEW_PANEL_TONE[entry.status] }}>
            <div style={reviewRowStyle}>
              <span style={{ ...microLabelStyle, marginBottom: 0 }}>Review</span>
              <ReviewStatusPill status={entry.status} size="md" />
              <div style={{ flex: 1 }} />
              {reviewActions}
            </div>
            {reviewLine && <div style={reviewLineStyle} title={reviewLineTitle}>{reviewLine}</div>}
            {entry.status === 'submitted' && wasResubmitted && (
              <div style={panelNoteStyle}>
                <strong>Re-submitted after an edit.</strong> It was approved before; the creator's changes sent it
                back for review.
              </div>
            )}
            {lastChange?.note && <div style={panelNoteStyle}>“{lastChange.note}”</div>}
            {confirmingDecline && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                <input
                  value={declineNote}
                  onChange={(e) => setDeclineNote(e.target.value)}
                  maxLength={2000}
                  placeholder="Feedback for the creator (optional — shown on their calendar page)"
                  aria-label="Decline note"
                  style={declineInputStyle}
                />
                <Button size="sm" variant="destructive" disabled={busy} onClick={() => moveTo('declined', declineNote || undefined)}>
                  Confirm decline
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDecline(false)}>Cancel</Button>
              </div>
            )}
          </div>

          <div style={factsStyle}>
            <Fact
              name="When"
              value={fmtPlannedRange(entry)}
              sub={recurrence === 'unknown' ? undefined : <span>{RECURRENCE_LABELS[recurrence]}</span>}
            />
            <Fact
              name="Moment"
              value={
                entry.customLabel ? `✦ ${entry.customLabel}`
                  : tentpole ? tentpoleLabel(tentpole)
                    : entry.tentpoleSlug || '—'
              }
              sub={
                entry.customLabel ? 'Custom moment, not a tentpole yet'
                  : tentpole ? fmtTentpoleWindow(tentpole)
                    : entry.tentpoleSlug ? 'Tentpole removed' : undefined
              }
            >
              {entry.customLabel && canManage && (
                <Button
                  size="sm"
                  variant="subtle"
                  style={{ marginTop: 8 }}
                  disabled={busy || !fullEntryLoaded}
                  onClick={() => onPromote(entry)}
                >
                  ✦ Make this a tentpole
                </Button>
              )}
            </Fact>
            <Fact
              name="Format"
              value={CONTENT_TYPE_LABELS[entry.contentType]}
              sub={planStatus === 'unknown' ? undefined : <>Plan: <span>{PLAN_STATUS_LABELS[planStatus]}</span></>}
            />
          </div>

          <div>
            <div style={microLabelStyle}>Who</div>
            <div style={whoGridStyle}>
              <span style={whoKeyStyle}>{channelLabels.length === 1 ? 'Channel' : 'Channels'}</span>
              <div style={{ minWidth: 0 }}>
                {targetScopeLabel(entry)}
                {entry.scopeMode === 'all_page_snapshot' && channelLabels.length > 0 && (
                  <div style={factSubStyle}>{channelLabels.join(' · ')}</div>
                )}
              </div>
              {/* A page named like its one channel would only repeat it. */}
              {entry.creatorLabel !== targetScopeLabel(entry) && (
                <>
                  <span style={whoKeyStyle}>Creator page</span>
                  <span title={entry.creatorSlug}>{entry.creatorLabel}</span>
                </>
              )}
            </div>
          </div>

          <div>
            <div style={{ ...microLabelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              {CALENDAR_FIELD_LABELS.advertiserSummary}
              <span style={shareTagStyle}>Shareable</span>
            </div>
            <div style={bodyTextStyle}>
              {advertiserSummaryOf(entry) || <span style={{ color: 'var(--fg-3)' }}>Nothing written yet.</span>}
            </div>
          </div>

          {entry.creatorPrivateNote && (
            <div>
              <div style={microLabelStyle}>{CALENDAR_FIELD_LABELS.creatorPrivateNote}</div>
              <div style={{ ...noteBoxStyle, background: 'var(--warning-soft)' }}>{entry.creatorPrivateNote}</div>
            </div>
          )}

          {entry.internalNote && (
            <div>
              <div style={microLabelStyle}>{CALENDAR_FIELD_LABELS.internalNote}</div>
              <div style={{ ...noteBoxStyle, background: 'var(--cg-neutral-50)' }}>{entry.internalNote}</div>
            </div>
          )}

          {statusHistory.length > 0 && (
            <div>
              <button
                type="button"
                style={disclosureStyle}
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((open) => !open)}
              >
                {historyOpen ? 'Hide history' : `Show history (${statusHistory.length})`}
              </button>
              {historyOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {[...statusHistory].reverse().map((h, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
                      {fmtDateTime(h.at)} — <strong>{ENTRY_STATUS_LABELS[h.from]}</strong> →{' '}
                      <strong>{ENTRY_STATUS_LABELS[h.to]}</strong> by {h.byEmail}
                      {h.note ? ` — “${h.note}”` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const microLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  marginBottom: 6,
};

const reviewPanelStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 'var(--radius-md)',
};

/** Only a plan that waits on CG gets color; decided ones stay neutral. */
const REVIEW_PANEL_TONE: Record<CalendarEntryStatus, React.CSSProperties> = {
  submitted: { background: 'var(--warning-soft)', border: '1px solid var(--warning)' },
  approved: { background: 'transparent', border: '1px solid var(--border-subtle)' },
  declined: { background: 'var(--cg-neutral-50)', border: '1px solid var(--border-subtle)' },
  withdrawn: { background: 'var(--cg-neutral-50)', border: '1px solid var(--border-subtle)' },
};

const reviewRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const reviewLineStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 12.5,
  color: 'var(--fg-2)',
};

const panelNoteStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--fg-2)',
};

const declineInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '8px 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  background: 'var(--bg-surface-1)',
  fontSize: 13,
};

const factsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: 16,
  padding: '14px 0',
  borderTop: '1px solid var(--border-subtle)',
  borderBottom: '1px solid var(--border-subtle)',
};

const factValueStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.35,
  color: 'var(--fg-1)',
};

const factSubStyle: React.CSSProperties = {
  marginTop: 3,
  fontSize: 12.5,
  lineHeight: 1.4,
  color: 'var(--fg-3)',
};

const whoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '110px minmax(0, 1fr)',
  columnGap: 12,
  rowGap: 6,
  fontSize: 14,
  color: 'var(--fg-1)',
};

const whoKeyStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-3)',
};

/** Marks the one field that may leave CG, against the confidential notes below. */
const shareTagStyle: React.CSSProperties = {
  padding: '1px 7px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--success-soft)',
  color: 'var(--success)',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'none',
};

const bodyTextStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--fg-2)',
  whiteSpace: 'pre-wrap',
};

const noteBoxStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-md)',
  padding: '10px 12px',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--fg-1)',
  whiteSpace: 'pre-wrap',
};

/** A text link, so it opts out of the global button hover lift. */
const disclosureStyle: React.CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  transform: 'none',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  cursor: 'pointer',
};

export default EntryDetail;
